import type {
  AdAudienceResult,
  AdAudienceCredentialType,
  CdpOutResult,
  DataOutResult,
  DataOutExporterType,
  ObjectTypeResult,
} from '@/services/auth/types';
import type {
  AudienceCredential,
  CdpOutIntegration,
  DataOutIntegration,
  MParticleIntegration,
  SegmentOutIntegration,
  ObjectType,
} from '@customerio/hydra-ui-api';
import { filterBySearchTerms, matchesSearchTerms } from './search-filter';

// ─── Ad Audiences ────────────────────────────────────────────────────────────

const AD_AUDIENCE_DISPLAY_NAMES: Record<AdAudienceCredentialType, string> = {
  facebook: 'Facebook Ads',
  adwords: 'Google Ads',
};

const AD_AUDIENCE_SEARCH_ALIASES: Record<AdAudienceCredentialType, string[]> = {
  facebook: ['facebook ads', 'facebook', 'meta ads'],
  adwords: ['google ads', 'google', 'adwords'],
};

/**
 * Maps a AudienceCredential to an AdAudienceResult.
 * Credentials are surfaced as the search target (shape decision: option a).
 * The URL links to the integrations audiences settings page for the credential type.
 */
export function mapAudienceCredentialToSearchResult(
  credential: AudienceCredential,
  workspaceId: number,
): AdAudienceResult {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const credType = credential.type as AdAudienceCredentialType;
  return {
    kind: 'ad_audience',
    doc_id: `ad_audience-${credential.id}`,
    title: AD_AUDIENCE_DISPLAY_NAMES[credType] ?? credential.type,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/audiences/${credential.type}/settings`,
    other: {
      credential_type: credType,
      expired: credential.expired,
      created_at: credential.created_at > 0 ? credential.created_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for ad audience credentials. Matches against display name and type aliases.
 */
export function filterAudienceCredentials(credentials: AudienceCredential[], query: string): AudienceCredential[] {
  return filterBySearchTerms(credentials, query, {
    getTerms: (credential) => {
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      return AD_AUDIENCE_SEARCH_ALIASES[credential.type as AdAudienceCredentialType] ?? [credential.type];
    },
  });
}

// ─── CDP Out ─────────────────────────────────────────────────────────────────

const CDP_OUT_SEARCH_TERMS = ['cdp event forwarding', 'cdp', 'forwarding', 'segment out', 'event forwarding'];

/**
 * Maps a CdpOutIntegration to a CdpOutResult.
 * CDP Out is a singleton per workspace; its fixed display name is "CDP Event Forwarding".
 * The URL points to the Journeys cdp_out settings page.
 *
 * The query is matched against the name "CDP Event Forwarding" and common synonyms
 * ("CDP", "forwarding", "segment") so users can find it without knowing the exact label.
 */
export function mapCdpOutToSearchResult(cdpOut: CdpOutIntegration, workspaceId: number): CdpOutResult {
  return {
    kind: 'cdp_out',
    doc_id: `cdp_out-${cdpOut.id}`,
    title: 'CDP event forwarding',
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/cdp_out`,
    other: {
      enabled: cdpOut.enabled,
      created_at: cdpOut.created_at > 0 ? cdpOut.created_at * 1000 : undefined,
    },
  };
}

/**
 * Returns whether the query matches the CDP Out integration.
 * CDP Out is a singleton, so this is a simple substring/fuzzy check against fixed terms.
 */
export function matchesCdpOut(query: string): boolean {
  return matchesSearchTerms(query, CDP_OUT_SEARCH_TERMS);
}

// ─── Data Out ─────────────────────────────────────────────────────────────────

/** Navigation URL per data-out exporter type. */
const DATA_OUT_URLS: Record<DataOutExporterType, (workspaceId: number) => string> = {
  mixpanel: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/data_out/mixpanel`,
  amplitude: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/data_out/amplitude`,
  segment_out: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/segment_out`,
  mparticle: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/mparticle_out`,
};

const DATA_OUT_DISPLAY_NAMES: Record<DataOutExporterType, string> = {
  mixpanel: 'Mixpanel',
  amplitude: 'Amplitude',
  segment_out: 'Segment',
  mparticle: 'mParticle',
};

const DATA_OUT_SEARCH_ALIASES: Record<DataOutExporterType, string[]> = {
  mixpanel: ['mixpanel'],
  amplitude: ['amplitude'],
  segment_out: ['segment', 'segment out', 'segment_out'],
  mparticle: ['mparticle', 'm particle'],
};

function makeDataOutResult(
  exporterType: DataOutExporterType,
  id: number,
  enabled: boolean,
  createdAt: number,
  workspaceId: number,
): DataOutResult {
  return {
    kind: 'data_out',
    doc_id: `data_out-${exporterType}-${id}`,
    title: DATA_OUT_DISPLAY_NAMES[exporterType],
    url: DATA_OUT_URLS[exporterType](workspaceId),
    other: {
      exporter_type: exporterType,
      enabled,
      created_at: createdAt > 0 ? createdAt * 1000 : undefined,
    },
  };
}

export function mapDataOutToSearchResult(integration: DataOutIntegration, workspaceId: number): DataOutResult {
  return makeDataOutResult(
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    integration.identifier as DataOutExporterType,
    integration.id,
    integration.enabled,
    integration.created_at,
    workspaceId,
  );
}

export function mapMParticleToSearchResult(integration: MParticleIntegration, workspaceId: number): DataOutResult {
  return makeDataOutResult('mparticle', integration.id, integration.enabled, integration.created_at, workspaceId);
}

export function mapSegmentOutToSearchResult(integration: SegmentOutIntegration, workspaceId: number): DataOutResult {
  return makeDataOutResult('segment_out', integration.id, integration.enabled, integration.created_at, workspaceId);
}

/**
 * Client-side filter for data-out integrations. Matches against display name, exporter slug, and aliases.
 */
export function filterDataOutResults(results: DataOutResult[], query: string): DataOutResult[] {
  return filterBySearchTerms(results, query, {
    getTerms: (result) => {
      const aliases = DATA_OUT_SEARCH_ALIASES[result.other.exporter_type];
      return [result.title, result.other.exporter_type, ...aliases];
    },
  });
}

// ─── Object Types ─────────────────────────────────────────────────────────────

/**
 * Client-side filter for object types. Matches against name, singular_name, and slug.
 * Only returns enabled types.
 */
export function filterObjectTypes(objectTypes: ObjectType[], query: string, cap: number): ObjectType[] {
  return filterBySearchTerms(objectTypes, query, {
    cap,
    getTerms: (objectType) => [objectType.name, objectType.singular_name, objectType.slug],
    shouldInclude: (objectType) => objectType.enabled,
  });
}

/**
 * Maps a ObjectType to an ObjectTypeResult.
 * Links to the objects list page for the given slug.
 */
export function mapObjectTypeToSearchResult(objectType: ObjectType, workspaceId: number): ObjectTypeResult {
  return {
    kind: 'object_type',
    doc_id: `object_type-${objectType.id}`,
    title: objectType.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/objects/${objectType.slug}`,
    other: {
      id: objectType.id,
      env_id: workspaceId,
      slug: objectType.slug,
      singular_name: objectType.singular_name,
      object_count: objectType.object_count,
      icon: objectType.icon ?? undefined,
    },
  };
}
